import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TablesTranslations } from '~shared/components/tables/tables-translations.service';
import { PaginatedTableComponent } from '~shared/components/tables/paginated-table/paginated-table.component';
import { Club, Customer, CustomerSearchHistory, CustomerSearchType, SidebarOptions } from '~core/models';
import { CustomerService, NavigationService, StorageService } from '~core/services';
import { TooltipTranslationService } from '~shared/translations';
import { SidebarService } from '~core/components/sidebar/sidebar.service';

/**
 * Initialize the base search component.
 */
@UntilDestroy()
@Component({
  selector: 'app-search-customer',
  host: { class: 'full-width' },
  templateUrl: 'search-customer.component.html'
})

export class SearchCustomerComponent implements OnDestroy, OnInit {
  @ViewChild(PaginatedTableComponent) paginatedTable: PaginatedTableComponent;

  public searchHistory: Array<any> = [];
  public selectedClub: Club;
  public isOpened: boolean = false;
  public customers: Array<Customer> = [];

  public goToCustomer: boolean = false;

  public searchOptions: any;
  public baseApi: string;
  public customQueries: Array<any> = [];

  public columns: Array<string> = [];
  public keys: Array<Array<string>> = [];
  public i18nEmptyState: string = 'No customers found';

  constructor(
    public navigationService: NavigationService,
    public sidebarService: SidebarService,
    public router: Router,
    public route: ActivatedRoute,
    public storage: StorageService,
    public customerService: CustomerService,
    public tooltipTranslations: TooltipTranslationService,
    private tablesTranslations: TablesTranslations,
  ) {
    this.navigationService.setCurrentBreadcrumbs(['crm', 'customerSearch']);
    this.sidebarService.setSidebar(new SidebarOptions(false, true));
    this.sidebarService.castIsOpened.pipe(untilDestroyed(this))
      .subscribe(sidebarOptions => this.isOpened = sidebarOptions.isOpened);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
    this.storage.castCustomerSearchHistory.pipe(untilDestroyed(this))
      .subscribe(history => {
        if (history && history[this.selectedClub.id]) {
          this.searchHistory = history[this.selectedClub.id];
        }
      });
    this.customerService.castCustomerSearchResults.pipe(untilDestroyed(this))
      .subscribe(customers => this.customers = customers);
  }

  public ngOnInit(): void {
    this.setUp();
    this.getCustomerEndpoint(this.selectedClub.id);

    this.customerService.castSearchOptions.pipe(untilDestroyed(this))
      .subscribe(options => {
        if (options) {
          this.searchOptions = options;
          this.searchForCustomers();
        }
      });
  }

  public ngOnDestroy(): void {
    this.customerService.setCustomerSearchResults(null);
    this.customerService.lastSearch = {};
  }

  /**
   * Performs the search if you click on the search history.
   */
  public searchFromHistory(item: any): void {
    this.customQueries.length = 0;
    this.customerService.setLastSearchResults(item);
    this.goToCustomer = true;
    if (item.basic) {
      this.router.navigate(['./basic'], { relativeTo: this.route });
    } else {
      this.router.navigate(['./advanced'], { relativeTo: this.route });
    }
  }

  /**
   * Adds / removes search values to the local storage.
   */
  private addToLocalStorage(query: string, values: Array<string>, basic: boolean): void {
    const date: Date = new Date();
    if (this.searchHistory) {
      const maxArrayLength = 5;
      this.searchHistory.forEach((value, index) => {
        if (value.query === query) {
          this.searchHistory.splice(index, 1);
        }
      });
      if (this.searchHistory.length < maxArrayLength) {
        this.searchHistory.unshift(new CustomerSearchHistory(query, values, basic, date));
        this.storage.setCustomerSearchHistory(this.searchHistory, this.selectedClub.id);
      } else {
        this.searchHistory = this.searchHistory.slice(0, -1);
        this.searchHistory.unshift(new CustomerSearchHistory(query, values, basic, date));
        this.storage.setCustomerSearchHistory(this.searchHistory, this.selectedClub.id);
      }
    } else {
      this.searchHistory.push(new CustomerSearchHistory(query, values, basic, date));
      this.storage.setCustomerSearchHistory(this.searchHistory, this.selectedClub.id);
    }
  }

  /**
   * Switch between basic and advanced search
   */
  public switchSearch(basic: boolean): void {
    if (basic) {
      this.router.navigate(['./basic'], { relativeTo: this.route });
    } else {
      this.router.navigate(['./advanced'], { relativeTo: this.route });
    }
    this.customerService.setCustomerSearchResults(null);
  }

  /**
   * Switch between basic and advanced search
   */
  public checkSearch(type: string): any {
    return this.router.url === `/club/${this.selectedClub.id}/search/${type}` ? true : null;
  }

  /**
   * Navigate to the create customer page
   */
  public navigateToCreateCustomer(): void {
    this.router.navigate([`/club/${this.selectedClub.id}/create-customer`]);
  }

  public setCustomers(customers: any): void {
    const createdCustomers = this.customerService.createCustomerObject(customers);
    this.customerService.setCustomerSearchResults(createdCustomers);

    if (this.customers.length === 1 && this.goToCustomer) {
      this.router.navigate([`/club/${this.selectedClub.id}/customer`, this.customers[0].id]);
    }

    this.goToCustomer = false;
  }

  public searchForCustomers() {
    if (this.paginatedTable) {
      this.customQueries.length = 0;
      if (this.searchOptions.type === CustomerSearchType.BASIC) {

        this.paginatedTable.baseApi = this.customerService.getCustomerSearchEndpoint(true, this.selectedClub.id)[0];
        this.customQueries.push({ term: this.searchOptions.query });
        this.goToCustomer = this.goToCustomer ? this.goToCustomer : this.searchOptions.byEnter;

        this.refetchTable();
        this.addToLocalStorage(`term=${this.searchOptions.query}`, this.searchOptions.query, this.searchOptions.type === CustomerSearchType.BASIC);

      } else if (this.searchOptions.type === CustomerSearchType.ADVANCED) {

        this.paginatedTable.baseApi = this.customerService.getCustomerSearchEndpoint(false, this.selectedClub.id)[0];
        this.customQueries.push(this.searchOptions.query);

        this.refetchTable();
        this.addToLocalStorage(JSON.stringify(this.searchOptions.query), this.searchOptions.query, this.searchOptions.type === CustomerSearchType.BASIC);
      }
    }
  }

  /**
   * Adds the columns and keys for the table
   */
  protected setUp(): void {
    this.columns.push(
      '',
      this.tablesTranslations.i18nCustNr,
      this.tablesTranslations.i18nCompany,
      this.tablesTranslations.i18nFirstName,
      this.tablesTranslations.i18nLastName,
      this.tablesTranslations.i18nEmail,
      this.tablesTranslations.i18nCity,
      this.tablesTranslations.i18nBirthday,
      this.tablesTranslations.i18nStatus,
    );
    this.keys.push(['membershipIcon', 'externalNumberBadge', 'company', 'firstName',
      'lastName', 'searchEmail', 'addresses[0].city', 'birthday', 'state.status']);
  }

  public rowClicked(event): void {
    this.router.navigate([`/club/${this.selectedClub.id}/customer`, event.id]);
  }

  /**
   * Get the customer endpoint.
   */
  public getCustomerEndpoint(id: number, isBasic: boolean = true): any {
    const endpoint = this.customerService.getCustomerSearchEndpoint(isBasic, id);
    this.baseApi = endpoint[0];
    this.customQueries = endpoint[1];
  }

  public refetchTable() {
    this.paginatedTable?.refetchData();
  }

}
